
import { useState } from "react";
import { PortfolioData, PortfolioItem } from "@/pages/DigitalPortfolio";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  X, 
  ArrowUp, 
  ArrowDown, 
  Trash2, 
  Link, 
  Tag, 
  Image,
  FileText,
  Video,
  Briefcase
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface PortfolioEditorProps {
  portfolio: PortfolioData;
  updatePortfolio: (updates: Partial<PortfolioData>) => void;
}

export const PortfolioEditor = ({
  portfolio,
  updatePortfolio
}: PortfolioEditorProps) => {
  const [newTag, setNewTag] = useState("");
  const [editingItem, setEditingItem] = useState<PortfolioItem | null>(null);

  const updateItem = (id: string, updates: Partial<PortfolioItem>) => {
    const updatedItems = portfolio.items.map(item => 
      item.id === id ? { ...item, ...updates } : item
    );
    updatePortfolio({ items: updatedItems });
    
    if (editingItem && editingItem.id === id) {
      setEditingItem({ ...editingItem, ...updates });
    }
  };

  const deleteItem = (id: string) => {
    const updatedItems = portfolio.items.filter(item => item.id !== id);
    updatePortfolio({ items: updatedItems });
    
    if (editingItem && editingItem.id === id) {
      setEditingItem(null);
    }
  };

  const moveItem = (id: string, direction: "up" | "down") => {
    const index = portfolio.items.findIndex(item => item.id === id);
    if (
      (direction === "up" && index === 0) || 
      (direction === "down" && index === portfolio.items.length - 1)
    ) {
      return;
    }
    
    const newIndex = direction === "up" ? index - 1 : index + 1;
    const updatedItems = [...portfolio.items];
    const item = updatedItems[index];
    updatedItems.splice(index, 1);
    updatedItems.splice(newIndex, 0, item);
    
    updatePortfolio({ items: updatedItems });
  };

  const addTag = (id: string) => {
    if (!newTag.trim()) return;
    
    const item = portfolio.items.find(item => item.id === id);
    if (!item) return;
    
    const tags = item.tags || [];
    if (tags.includes(newTag.trim())) {
      setNewTag("");
      return;
    }
    
    updateItem(id, { tags: [...tags, newTag.trim()] });
    setNewTag("");
  };

  const removeTag = (id: string, tag: string) => {
    const item = portfolio.items.find(item => item.id === id);
    if (!item || !item.tags) return;
    
    updateItem(id, { tags: item.tags.filter(t => t !== tag) });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "image": return <Image className="h-5 w-5" />;
      case "text": return <FileText className="h-5 w-5" />;
      case "video": return <Video className="h-5 w-5" />;
      case "project": return <Briefcase className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  if (portfolio.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-10 text-center">
        <div className="w-20 h-20 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
          <Image className="h-10 w-10 text-indigo-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Add your first portfolio item</h3>
        <p className="text-gray-500 max-w-md mb-6">
          Start building your portfolio by adding images, text, videos, or projects using the tools in the sidebar.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
      {/* Items List */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Portfolio Items</h3>
        
        <div className="space-y-3">
          {portfolio.items.map((item, index) => (
            <Card 
              key={item.id} 
              className={`border ${editingItem?.id === item.id ? 'border-indigo-400 ring-1 ring-indigo-400' : 'border-gray-200'}`}
            >
              <CardHeader className="p-4 pb-0 flex flex-row items-center space-y-0">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  {getTypeIcon(item.type)}
                  <span className="truncate">{item.title || `Untitled ${item.type}`}</span>
                </CardTitle>
                
                <div className="flex items-center gap-1 ml-auto">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => moveItem(item.id, "up")}
                    disabled={index === 0}
                    className="h-8 w-8 p-0"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => moveItem(item.id, "down")}
                    disabled={index === portfolio.items.length - 1}
                    className="h-8 w-8 p-0"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setEditingItem(item)}
                    className="h-8 w-8 p-0"
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => deleteItem(item.id)}
                    className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent className="p-4">
                {item.type === "image" && item.content && (
                  <div className="aspect-video bg-gray-100 rounded-md overflow-hidden mb-2">
                    <img 
                      src={item.content} 
                      alt={item.title || "Portfolio image"} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                {item.type === "video" && item.content && (
                  <div className="aspect-video bg-gray-100 rounded-md overflow-hidden mb-2 flex items-center justify-center">
                    <Video className="h-10 w-10 text-gray-400" />
                  </div>
                )}
                
                <p className="text-sm text-gray-500 line-clamp-2 mt-2">
                  {item.description || "No description provided."}
                </p>
                
                {item.tags && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {item.tags.map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      
      {/* Item Editor */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">
          {editingItem ? `Edit ${editingItem.type}` : "Select an item to edit"}
        </h3>
        
        {editingItem ? (
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Title</label>
                <Input 
                  value={editingItem.title || ""} 
                  onChange={(e) => updateItem(editingItem.id, { title: e.target.value })}
                  placeholder="Enter a title for this item"
                />
              </div>
              
              {editingItem.type === "text" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Content</label>
                  <Textarea 
                    value={editingItem.content || ""} 
                    onChange={(e) => updateItem(editingItem.id, { content: e.target.value })}
                    placeholder="Enter your text content here"
                    rows={5}
                  />
                </div>
              )}
              
              {editingItem.type === "video" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Video URL</label>
                  <div className="flex gap-2">
                    <Input 
                      value={editingItem.content || ""} 
                      onChange={(e) => updateItem(editingItem.id, { content: e.target.value })}
                      placeholder="Enter YouTube or Vimeo URL"
                      className="flex-1"
                    />
                    <Button variant="outline" size="sm">
                      <Link className="h-4 w-4 mr-1" />
                      Embed
                    </Button>
                  </div>
                </div>
              )}
              
              {editingItem.type === "project" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Project Link</label>
                  <Input 
                    value={editingItem.link || ""} 
                    onChange={(e) => updateItem(editingItem.id, { link: e.target.value })}
                    placeholder="Enter project URL"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea 
                  value={editingItem.description || ""} 
                  onChange={(e) => updateItem(editingItem.id, { description: e.target.value })}
                  placeholder="Describe this portfolio item"
                  rows={3}
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center">
                  <Tag className="h-4 w-4 mr-1" />
                  Tags
                </label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {editingItem.tags?.map(tag => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => removeTag(editingItem.id, tag)}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={newTag} 
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag"
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag(editingItem.id);
                      }
                    }}
                  />
                  <Button 
                    onClick={() => addTag(editingItem.id)}
                    disabled={!newTag.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline"
                  onClick={() => setEditingItem(null)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => deleteItem(editingItem.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Item
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center flex flex-col items-center justify-center h-[400px]">
            <FileText className="h-12 w-12 text-gray-400 mb-4" />
            <h4 className="text-lg font-medium mb-2">No item selected</h4>
            <p className="text-gray-500 max-w-md">
              Select an item from the list on the left to edit its details.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
